import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Organisation } from '@shared/api/session.api'
import { AnchorStatus, SupplierStatus } from '@shared/domain/status'

const ACTIVE_ANCHOR: string[] = [
  AnchorStatus.ACTIVE,
  AnchorStatus.ANCHOR_STATUS_ACTIVE,
  AnchorStatus.AVAILABLE_FOR_SUPPLIER_ONBOARDING,
]
const ACTIVE_SUPPLIER: string[] = [
  SupplierStatus.ACTIVE,
  SupplierStatus.SUPPLIER_APPROVED_FOR_PROGRAMME,
]

export const useOrgStore = defineStore('org', () => {
  const organisation = ref<Organisation | null>(null)

  const activationStatus = computed(() => organisation.value?.activationStatus)
  const activeProgrammeId = computed(() => organisation.value?.activeProgrammeId)

  /**
   * Anchors before ANCHOR_STATUS_ACTIVE and suppliers before approval are not yet active.
   * An unknown status (the login response does not carry one yet) does not gate the UI;
   * the backend still enforces what the org may do.
   */
  const isActive = computed(() => {
    const org = organisation.value
    if (!org) return false
    const status = org.activationStatus
    if (org.type === 'ops' || status === undefined) return true
    return org.type === 'anchor' ? ACTIVE_ANCHOR.includes(status) : ACTIVE_SUPPLIER.includes(status)
  })

  function setOrganisation(org: Organisation) {
    organisation.value = org
  }

  function setActivationStatus(status: string) {
    if (organisation.value) organisation.value = { ...organisation.value, activationStatus: status }
  }

  function $reset() {
    organisation.value = null
  }

  return {
    organisation,
    activationStatus,
    activeProgrammeId,
    isActive,
    setOrganisation,
    setActivationStatus,
    $reset,
  }
})
